import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { getImageUrl } from '@deepguide-ai/shared';
import { InteractionPoint } from '../screenshot-extension';
import { InteractionPointsManager } from '../plate-ui/interaction-point';
import { Resizable } from '../resizable';
import { Move } from 'lucide-react';

export interface ScreenshotRendererProps {
  url: string;
  width: number;
  height: number;
  elementBox?: any;
  interactionPoints?: InteractionPoint[];
  cropStyle?: 'cropped' | 'full' | 'none';
  align?: 'left' | 'center' | 'right';
  alt?: string;
  zoomLevel?: number;
  panX?: number;
  panY?: number;
  onZoomChange?: (zoom: number) => void;
  onAlignChange?: (align: 'left' | 'center' | 'right') => void;
  onWidthChange?: (width: number) => void;
  onPanChange?: (panX: number, panY: number) => void;
  readOnly?: boolean;
  isFullScreenshot?: boolean; // For standalone Screenshot elements
  className?: string;
}

export function ScreenshotRenderer({
  url,
  width,
  height,
  interactionPoints = [],
  cropStyle = 'full',
  align = 'center',
  alt = '',
  zoomLevel = 1,
  panX = 0,
  panY = 0,
  onZoomChange,
  onAlignChange,
  onWidthChange,
  onPanChange,
  readOnly = true,
  isFullScreenshot = false,
  className,
}: ScreenshotRendererProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const imageRef = React.useRef<HTMLDivElement>(null);

  const screenshotSize = useMemo(
    () => ({ width, height }),
    [width, height]
  );

  const imageStyle = useMemo(() => {
    const cursor = zoomLevel > 1 && !readOnly ? (isDragging ? 'grabbing' : 'grab') : 'default';

    // Use contain to ensure image fills container exactly (since container has matching aspect ratio)
    // This guarantees 1:1 mapping between image pixels and overlay percentages
    return {
      objectFit: "contain" as const,
      objectPosition: "center",
      width: '100%',
      height: '100%',
      cursor,
    };
  }, [zoomLevel, isDragging, readOnly]);


  const handleWidthChange = useCallback(
    (newWidth: number) => {
      if (onWidthChange) {
        onWidthChange(newWidth);
      }
    },
    [onWidthChange]
  );

  // Handle drag start
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (readOnly || zoomLevel <= 1) return;

      // Check if we clicked on the image itself, not a resize handle
      const target = e.target as HTMLElement;
      if (!target.tagName || target.tagName !== 'IMG') return;

      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({ x: e.clientX - panX, y: e.clientY - panY });
    },
    [readOnly, zoomLevel, panX, panY]
  );

  // Handle drag move
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!onPanChange) return;
      const newPanX = e.clientX - dragStart.x;
      const newPanY = e.clientY - dragStart.y;
      onPanChange(newPanX, newPanY);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, onPanChange]);

  // Handle tooltip
  const handleMouseEnter = useCallback(() => {
    if (!readOnly && zoomLevel > 1) {
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 2000); // Hide after 2 seconds
    }
  }, [readOnly, zoomLevel]);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  // For Step integration, we don't need resizable wrapper
  if (!isFullScreenshot) {
    return (
      <div
        ref={imageRef}
        className={cn(
          "relative overflow-hidden rounded-md border border-border",
          className
        )}
        style={{
          // Container uses same aspect ratio as image, ensuring image fills it completely
          aspectRatio: `${screenshotSize.width} / ${screenshotSize.height}`,
        }}
      >
        {/* Tooltip - outside transform container */}
        {showTooltip && !isDragging && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-sm pointer-events-none z-30 flex items-center gap-1 border border-white/10">
            <Move className="w-3 h-3" />
            <span>Drag to Pan</span>
          </div>
        )}

        <img
          src={getImageUrl(url, 1920, 100)}
          alt={alt}
          onLoad={() => setImageLoaded(true)}
          onMouseDown={handleMouseDown}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className="w-full h-full user-select-none transition-transform duration-300"
          style={{
            ...imageStyle,
            transform: zoomLevel > 1 ? `translate(${panX}px, ${panY}px) scale(${zoomLevel})` : undefined,
            transformOrigin: "center",
          }}
          draggable={false}
        />

        {interactionPoints.length > 0 && (
          <div
            className="absolute top-0 left-0 w-full h-full pointer-events-none transition-opacity duration-300"
            style={{
              opacity: imageLoaded ? 1 : 0,
              transform: zoomLevel > 1 ? `translate(${panX}px, ${panY}px) scale(${zoomLevel})` : 'none',
              transformOrigin: "center",
            }}
          >
            <InteractionPointsManager
              points={interactionPoints}
              containerWidth={screenshotSize.width}
              containerHeight={screenshotSize.height}
            />
          </div>
        )}
      </div>
    );
  }

  // Full resizable version for standalone Screenshot elements
  return (
    <Resizable
      align={align}
      width={width}
      onWidthChange={handleWidthChange}
      readOnly={readOnly}
    >
      <figure className="group relative m-0 max-w-full">
        <div
          ref={imageRef}
          className="relative overflow-hidden rounded-md border border-border"
          style={{
            // Container uses same aspect ratio as image, ensuring image fills it completely
            aspectRatio: `${screenshotSize.width} / ${screenshotSize.height}`,
          }}
        >
          {/* Tooltip - outside transform container */}
          {showTooltip && !isDragging && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-sm pointer-events-none z-30 flex items-center gap-1 border border-white/10">
              <Move className="w-3 h-3" />
              <span>Drag to Pan</span>
            </div>
          )}

          <img
            src={getImageUrl(url, 1920, 100)}
            alt={alt}
            onLoad={() => setImageLoaded(true)}
            onMouseDown={handleMouseDown}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="w-full h-full user-select-none transition-transform duration-300"
            style={{
              ...imageStyle,
              transform: zoomLevel > 1 ? `translate(${panX}px, ${panY}px) scale(${zoomLevel})` : undefined,
              transformOrigin: "center",
              willChange: 'transform',
            }}
            draggable={false}
          />

          {interactionPoints.length > 0 && (
            <div
              className="absolute top-0 left-0 w-full h-full pointer-events-none transition-opacity duration-300"
              style={{
                opacity: imageLoaded ? 1 : 0,
                transform: zoomLevel > 1 ? `translate(${panX}px, ${panY}px) scale(${zoomLevel})` : 'none',
                transformOrigin: "center",
              }}
            >
              <InteractionPointsManager
                points={interactionPoints}
                containerWidth={screenshotSize.width}
                containerHeight={screenshotSize.height}
              />
            </div>
          )}
        </div>
      </figure>
    </Resizable>
  );
}
